/**
 * ============================================================================
 * Iron Meridian - useTurnSummary Hook
 * ============================================================================
 * 
 * Helper hook that processes turn results and extracts data for TurnSummaryModal.
 * Transforms combat logs, economy changes, and events into display-ready format.
 */

import { useState, useCallback, useMemo } from 'react'

// ============================================================================
// AI INTENT TRANSLATION
// ============================================================================

const AI_ACTION_PHRASES = {
  attack: [
    '{country} launched an offensive',
    '{country} pursued military expansion',
    '{country} attacked aggressively'
  ],
  fortify: [
    '{country} strengthened defenses',
    '{country} focused on fortification',
    '{country} reinforced borders'
  ],
  build: [
    '{country} invested in infrastructure',
    '{country} expanded production capacity',
    '{country} built new facilities'
  ],
  research: [
    '{country} advanced technology',
    '{country} focused on research',
    '{country} prioritized innovation'
  ],
  hold: [
    '{country} maintained current positions',
    '{country} adopted a wait-and-see approach',
    '{country} consolidated power'
  ]
}

function translateAIAction(countryName, action) {
  const phrases = AI_ACTION_PHRASES[action] || AI_ACTION_PHRASES.hold
  const phrase = phrases[Math.floor(Math.random() * phrases.length)]
  return phrase.replace('{country}', countryName)
}

// ============================================================================
// DATA EXTRACTION FUNCTIONS
// ============================================================================

function extractCombatResults(turnResult, countries) {
  const combatResults = turnResult?.combatResults || turnResult?.combat || []
  
  return combatResults.map(combat => ({
    attacker: combat.attacker || combat.attacker_country_id,
    defender: combat.defender || combat.defender_country_id,
    outcome: combat.outcome || (combat.winner === combat.attacker ? 'victory' : 'defeat'),
    winner: combat.winner,
    attackerLosses: combat.attackerLosses ?? combat.attacker_losses ?? 0,
    defenderLosses: combat.defenderLosses ?? combat.defender_losses ?? 0,
    territoryChanged: combat.territoryChanged ?? combat.territory_changed ?? false
  }))
}

function extractEconomyChanges(previousState, currentState, countries, playerCountry) {
  const changes = []
  
  // Only show changes for player-owned countries
  if (!previousState || !currentState) return changes

  const previousCountries = previousState.countries || {}
  const currentCountries = currentState.countries || countries || {}

  for (const [countryId, current] of Object.entries(currentCountries)) {
    // Only show player's countries
    if (current.controller !== 'player' && current.owner_player_id !== playerCountry) {
      continue
    }

    const previous = previousCountries[countryId]
    if (!previous) continue

    const prevEconomy = previous.economy ?? 0
    const currEconomy = current.economy ?? 0

    if (prevEconomy !== currEconomy) {
      changes.push({
        country: { id: countryId, name: current.name || countryId },
        previous: prevEconomy,
        current: currEconomy
      })
    }
  }

  return changes
}

function extractBuildingsCompleted(turnResult, countries) {
  const buildings = []
  const buildingEvents = turnResult?.events?.filter(e => 
    e.type === 'building_completed' || e.type === 'construction_complete'
  ) || []

  for (const event of buildingEvents) {
    buildings.push({
      name: event.building || event.buildingType || 'Building',
      country: countries[event.country]?.name || event.country
    })
  }

  return buildings
}

function extractAIIntents(turnResult, countries) {
  const intents = []
  const aiActions = turnResult?.aiActions || turnResult?.ai_actions || []

  for (const action of aiActions) {
    const countryName = countries[action.country]?.name || action.country
    const actionType = action.action || action.type || 'hold'
    intents.push(translateAIAction(countryName, actionType))
  }

  // Limit to 5 most interesting actions
  return intents.slice(0, 5)
}

function extractMajorEvents(turnResult, combatResults, playerCountry) {
  const events = []

  // Check for rebellions
  const rebellions = turnResult?.events?.filter(e => e.type === 'rebellion') || []
  for (const rebellion of rebellions) {
    events.push(`Rebellion in ${rebellion.country}!`)
  }

  // Check for capital lost
  const capitalLost = turnResult?.events?.find(e => e.type === 'capital_lost')
  if (capitalLost) {
    events.push(`Capital lost! ${capitalLost.country} has fallen.`)
  }

  // Check for eliminations
  const eliminations = turnResult?.events?.filter(e => e.type === 'player_eliminated') || []
  for (const elim of eliminations) {
    events.push(`${elim.player || elim.country} has been eliminated!`)
  }

  // Check for player defeats in combat
  const playerDefeats = combatResults.filter(c => 
    c.attacker === playerCountry && c.outcome === 'defeat'
  )
  if (playerDefeats.length > 0) {
    events.push(`${playerDefeats.length} attack(s) failed`)
  }

  return events
}

function calculateTerritoryChanges(combatResults, playerCountry) {
  let gained = 0
  let lost = 0

  for (const combat of combatResults) {
    if (combat.territoryChanged || combat.outcome === 'victory') {
      if (combat.attacker === playerCountry) {
        gained++
      } else if (combat.defender === playerCountry) {
        lost++
      }
    }
    if (combat.outcome === 'defeat' && combat.defender !== playerCountry) {
      // AI captured from us
      if (combat.attacker !== playerCountry && combat.defender === playerCountry) {
        lost++
      }
    }
  }

  return { gained, lost }
}

function extractCrisisEvents(turnResult) {
  const crisisEvents = []
  
  // Extract crisis events from turn result
  if (turnResult?.events) {
    const crisisTypes = ['crisis_started', 'crisis_escalated', 'crisis_resolved']
    
    for (const event of turnResult.events) {
      if (crisisTypes.includes(event.type)) {
        crisisEvents.push({
          type: event.type,
          crisis_id: event.crisis_id,
          crisis_name: event.crisis_name || event.name,
          stage: event.stage,
          severity: event.severity,
          affected: event.affected || [],
          resolution_type: event.resolution_type,
          aftermath_duration: event.aftermath_duration
        })
      }
    }
  }
  
  return crisisEvents
}

function extractActiveCrises(turnResult) {
  // Get list of active crises from turn result
  return turnResult?.activeCrises || []
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export default function useTurnSummary() {
  const [isOpen, setIsOpen] = useState(false)
  const [summaryData, setSummaryData] = useState(null)

  /**
   * Process turn result and show summary
   */
  const showSummary = useCallback((turnResult, previousState, currentState, countries, playerCountry) => {
    const combatResults = extractCombatResults(turnResult, countries)
    const economyChanges = extractEconomyChanges(previousState, currentState, countries, playerCountry)
    const buildingsCompleted = extractBuildingsCompleted(turnResult, countries)
    const aiIntents = extractAIIntents(turnResult, countries)
    const majorEvents = extractMajorEvents(turnResult, combatResults, playerCountry)
    const { gained, lost } = calculateTerritoryChanges(combatResults, playerCountry)
    const crisisEvents = extractCrisisEvents(turnResult)
    const activeCrises = extractActiveCrises(turnResult)

    setSummaryData({
      turnNumber: currentState?.currentTurn || turnResult?.turn || 1,
      combatResults,
      economyChanges,
      buildingsCompleted,
      aiIntents,
      majorEvents,
      territoriesGained: gained,
      territoriesLost: lost,
      crisisEvents,
      activeCrises,
      countries
    })

    setIsOpen(true)
  }, [])

  /**
   * Close the summary modal
   */
  const closeSummary = useCallback(() => {
    setIsOpen(false)
  }, [])

  /**
   * Check if there's anything interesting to show
   */
  const hasContent = useMemo(() => {
    if (!summaryData) return false
    return (
      summaryData.combatResults.length > 0 ||
      summaryData.economyChanges.length > 0 ||
      summaryData.buildingsCompleted.length > 0 ||
      summaryData.majorEvents.length > 0
    )
  }, [summaryData])

  return {
    isOpen,
    summaryData,
    showSummary,
    closeSummary,
    hasContent
  }
}
